import { BsdsCheckbox } from '@bsds-lib-component/react';
import React from 'react';

import type { AdhesionProposalItem, AdhesionProposalTableProps } from '../../../types/vida/table/adhesionProposalTableType';
import { isAguardandoAceiteStatus, mapProponentStatus } from '../../../utils/proponentStatusUtils';
import { generateProposalsXLSX } from '../../../utils/proposalExcelUtils';
import { formatSubestipulanteCodNome } from '../../../utils/subestipulanteUtil';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableHeaderBulkActionsMenu from '../shared/TableHeaderBulkActionsMenu';
import TableItemActionsMenu from '../shared/TableItemActionsMenu';
import TableMenuItemButton from '../shared/TableMenuItemButton';
import { createMenuItemActionHandlers } from '../shared/tableMenuUtils';
import { useTableHeaderMenu } from '../shared/useTableHeaderMenu';
import { useTableItemMenu } from '../shared/useTableItemMenu';
import { useTableRowSelection } from '../shared/useTableRowSelection';

const truncateCellClass = 'px-4 py-2 max-w-0 truncate align-top';

const TruncateCell: React.FC<{ value: string; tooltip?: string }> = ({ value, tooltip }) => (
  <Tooltip label={tooltip ?? value}>
    <span className="block truncate">{value}</span>
  </Tooltip>
);

const AdhesionProposalTable: React.FC<AdhesionProposalTableProps> = ({
  proposals,
  formatDate,
  formatCpf,
  getStatusColor,
  onRowClick,
  emptyMessage = 'Nenhuma proposta encontrada',
  onSelectionChange,
  onExport,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const showRowActions = Boolean(onRowClick);

  const {
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
    registerItemActionRef,
  } = useTableItemMenu<number>();

  const {
    isMenuOpen,
    closeMenu,
    toggleMenu,
    menuRef,
    menuButtonRef,
    actionButtonRef,
    menuButtonId,
    handleMenuKeyDown,
    createActionKeyDownHandler,
  } = useTableHeaderMenu();

  const {
    allSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectItem,
    isSelected,
    getSelectedItems,
  } = useTableRowSelection({
    itemIds: proposals.map((proposal) => proposal.id),
    onSelectionChange,
  });

  const canOpenProposalDetail = (proposal: AdhesionProposalItem): boolean =>
    Boolean(onRowClick) && !isAguardandoAceiteStatus(proposal.status);

  const handleExport = async () => {
    if (!hasSelectedItems) {
      return;
    }

    const selectedProposals = getSelectedItems(proposals);

    if (onExport) {
      onExport(selectedProposals);
    } else {
      try {
        await generateProposalsXLSX(selectedProposals, formatDate, formatCpf, mapProponentStatus);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao exportar propostas em XLSX', error);
      }
    }

    closeMenu();
  };

  const consultActionHandlers = createMenuItemActionHandlers<AdhesionProposalItem>({
    onAction: (proposal) => onRowClick?.(proposal),
    closeMenu: closeItemMenu,
    focusMenuButton: (proposal) => focusMenuButton(proposal.id),
    canExecute: canOpenProposalDetail,
  });

  const tableColumnCount = showRowActions ? 9 : 8;

  const renderHeaderBulkMenu = (wrapTriggerWithTooltip = true) => (
    <TableHeaderBulkActionsMenu
      isOpen={isMenuOpen}
      menuButtonId={menuButtonId}
      menuRef={menuRef}
      menuButtonRef={menuButtonRef}
      actionButtonRef={actionButtonRef}
      actionLabel="Exportar em Excel"
      actionDisabled={!hasSelectedItems}
      wrapTriggerWithTooltip={wrapTriggerWithTooltip}
      onToggle={(event) => {
        event.stopPropagation();
        toggleMenu();
      }}
      onMenuKeyDown={handleMenuKeyDown}
      onAction={(event) => {
        event.stopPropagation();
        void handleExport();
      }}
      onActionKeyDown={createActionKeyDownHandler(() => void handleExport())}
    />
  );

  return (
    <>
      <div className="w-full max-w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <table
          className="min-w-[1340px] w-full table-fixed"
          aria-label="Propostas de adesão"
        >
          <thead>
            <tr>
              {showRowActions && (
                <th
                  className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16"
                  scope="col"
                  aria-label="Ações da tabela"
                >
                  {renderHeaderBulkMenu()}
                </th>
              )}
              <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                {!showRowActions ? (
                  <Tooltip label="Ações da tabela">
                    <div className="flex items-center gap-1">
                      {renderHeaderBulkMenu(false)}
                      <Tooltip label="Selecionar todas as propostas">
                        <div className="pl-2">
                          <BsdsCheckbox
                            checked={allSelected}
                            onBsdsChange={handleSelectAll}
                            size="small"
                            color="secondary"
                            aria-label="Selecionar todas as propostas"
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip label="Selecionar todas as propostas">
                    <BsdsCheckbox
                      checked={allSelected}
                      onBsdsChange={handleSelectAll}
                      size="small"
                      color="secondary"
                      aria-label="Selecionar todas as propostas"
                    />
                  </Tooltip>
                )}
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-20" scope="col">
                <Tooltip label="ID da Proposta">
                  <span>ID</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 w-52" scope="col">
                <Tooltip label="Status da proposta">
                  <span className="block truncate">Status</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 w-36" scope="col">
                <Tooltip label="Data do status">
                  <span className="block truncate">Data do Status</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-32" scope="col">
                <Tooltip label="CPF do proponente">
                  <span>CPF</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 w-52" scope="col">
                <Tooltip label="Nome do proponente">
                  <span className="block truncate">Proponente</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">
                <Tooltip label="Prazo de decurso">
                  <span>Prazo Decurso</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 pl-6 text-left text-sm font-semibold border-b border-gray-100 w-60" scope="col">
                <Tooltip label="Sub-Estipulante">
                  <span className="block truncate">Sub-Estipulante</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 text-text-color">
            {proposals && proposals.length > 0 ? (
              proposals.map((proposal) => {
                const proponentName = proposal.proposerName?.trim() || '—';
                const statusLabel = mapProponentStatus(proposal.status);
                const statusAtLabel = formatDate(proposal.statusAt);
                const cpfLabel = formatCpf(proposal.proposerCpf);
                const subestipulanteLabel = formatSubestipulanteCodNome(
                  proposal.subCompanyCod,
                  proposal.subCompanyName,
                );
                const isDetailEnabled = canOpenProposalDetail(proposal);

                return (
                  <tr
                    key={proposal.id}
                    className="hover:bg-gray-50 transition-colors text-xs"
                    aria-selected={isSelected(proposal.id)}
                  >
                    {showRowActions && (
                      <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <TableItemActionsMenu
                          itemId={proposal.id}
                          isOpen={isItemMenuOpen(proposal.id) && isDetailEnabled}
                          menuId={`proposal-menu-${proposal.id}`}
                          triggerLabel={
                            isDetailEnabled
                              ? `Ações da proposta ${proposal.id}`
                              : `Ações indisponíveis para proposta ${proposal.id} aguardando aceite`
                          }
                          menuLabel={`Menu de ações da proposta ${proposal.id}`}
                          triggerTooltip={
                            isDetailEnabled
                              ? `Ações da proposta ${proposal.id}`
                              : 'Detalhes indisponíveis para propostas aguardando aceite'
                          }
                          disabled={!isDetailEnabled}
                          buttonClassName={`rounded p-1 transition-colors focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 ${isDetailEnabled
                              ? 'hover:bg-gray-100 cursor-pointer'
                              : 'cursor-not-allowed opacity-50'
                            }`}
                          onToggle={(event) => handleItemMenuToggle(proposal.id, event)}
                          onKeyDown={(event) => handleItemMenuKeyDown(proposal.id, event)}
                          registerMenuRef={(element) => registerItemMenuRef(proposal.id, element)}
                          registerButtonRef={(element) => registerItemMenuButtonRef(proposal.id, element)}
                        >
                          <TableMenuItemButton
                            registerRef={(element) => registerItemActionRef(proposal.id, element)}
                            onClick={(event) => consultActionHandlers.handleClick(proposal, event)}
                            onKeyDown={(event) => consultActionHandlers.handleKeyDown(proposal, event)}
                          >
                            Consultar
                          </TableMenuItemButton>
                        </TableItemActionsMenu>
                      </td>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Selecionar proposta">
                        <BsdsCheckbox
                          checked={isSelected(proposal.id)}
                          onBsdsChange={() => handleSelectItem(proposal.id)}
                          size="small"
                          color="secondary"
                          aria-label={`Selecionar proposta ${proposal.id}`}
                        />
                      </Tooltip>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap align-top">{proposal.id}</td>
                    <td className="px-4 py-2 align-top">
                      <Tooltip label={statusLabel}>
                        <span
                          className="text-xs py-0.5 px-2 rounded-xs inline-block max-w-full truncate text-center font-medium"
                          style={{
                            borderLeft: `4px solid ${getStatusColor(proposal.status)}`,
                            backgroundColor: `${getStatusColor(proposal.status)}80`,
                            minWidth: '11rem',
                          }}
                        >
                          {statusLabel}
                        </span>
                      </Tooltip>
                    </td>
                    <td className={truncateCellClass}>
                      <TruncateCell value={statusAtLabel} />
                    </td>
                    <td className={truncateCellClass}>
                      <TruncateCell value={cpfLabel} />
                    </td>
                    <td className={truncateCellClass}>
                      <TruncateCell value={proponentName} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap align-top">
                      {proposal.prazoDec ? `${proposal.prazoDec} dias` : '-'}
                    </td>
                    <td className={`${truncateCellClass} pl-6`}>
                      <TruncateCell value={subestipulanteLabel} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  tabIndex={-1}
                  colSpan={tableColumnCount}
                  className="px-4 py-4 text-left sm:text-center text-text-color text-xs focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {onPageChange != null
        && currentPage != null
        && totalPages != null
        && totalItems != null
        && totalItems > 0 && (
          <div className="bsds-mt-16 bsds-mb-16">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              totalItems={totalItems}
              itemLabel="Propostas"
              alwaysShow
            />
          </div>
        )}
    </>
  );
};

export default AdhesionProposalTable;
